import { FC, ReactNode } from "react"
import {
  Box,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { UserbotCatalog } from "../../interfaces"
import { ARRAY_CAPTION_PREFIXES, ENUM_LABELS } from "../../constants/enumLabels"
import { isRootUnion, matchingVariant } from "./fieldSpecVariant"

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isUuid = (v: unknown): v is string =>
  typeof v === "string" && UUID_RE.test(v)

const ChainLink: FC<{ id: string }> = ({ id }) => {
  const navigate = useNavigate()
  return (
    <Link
      component="button"
      type="button"
      variant="body2"
      onClick={() => navigate(`/userbot/chains/report/${id}`)}
      sx={{ cursor: "pointer", textAlign: "left", fontFamily: "monospace" }}
    >
      открыть цепочку ({id.slice(0, 8)}…)
    </Link>
  )
}

const renderValue = (
  spec: UserbotCatalog.FieldSpec,
  val: unknown,
): ReactNode => {
  if (val === null || val === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  if (spec.type === "enum") {
    const raw = String(val)
    const label = ENUM_LABELS[raw] ?? raw
    return <Chip size="small" label={label} />
  }

  if (spec.type === "oneOf") {
    const variant = matchingVariant(spec.variants ?? [], val)
    return variant
      ? renderValue(variant, val)
      : "Неизвестный вариант результата"
  }

  if (spec.type === "json") {
    try {
      return (
        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
          {JSON.stringify(val)}
        </Typography>
      )
    } catch {
      return String(val)
    }
  }

  if (spec.type === "object" && spec.fields) {
    const obj = (val ?? {}) as Record<string, unknown>
    return (
      <Stack
        spacing={0.5}
        pl={1}
        sx={{ borderLeft: 1, borderColor: "divider" }}
      >
        {spec.fields.map((f) => (
          <Box key={f.key}>
            <Typography variant="caption" color="text.secondary">
              {f.label || f.key}:{" "}
            </Typography>
            {renderValue(f, obj[f.key])}
          </Box>
        ))}
      </Stack>
    )
  }

  if (spec.type === "array") {
    const arr = Array.isArray(val) ? val : []
    const prefix = ARRAY_CAPTION_PREFIXES[spec.key]
    const caption = prefix
      ? `${prefix}, ${arr.length} шт.`
      : `${arr.length} шт.`

    if (spec.item && spec.item.type === "object" && spec.item.fields) {
      const fields = spec.item.fields
      return (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            mb={0.5}
            display="block"
          >
            {caption}
          </Typography>
          {arr.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Пусто
            </Typography>
          ) : (
            <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
              <TableHead>
                <TableRow>
                  {fields.map((f) => (
                    <TableCell key={f.key}>{f.label || f.key}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {arr.map((row, i) => {
                  const r = (row ?? {}) as Record<string, unknown>
                  return (
                    <TableRow key={i}>
                      {fields.map((f) => (
                        <TableCell key={f.key}>
                          {renderValue(f, r[f.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </Box>
      )
    }

    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
        <Stack spacing={0.5} mt={0.5}>
          {arr.map((item, i) => (
            <Box key={i}>
              {spec.item ? renderValue(spec.item, item) : String(item)}
            </Box>
          ))}
        </Stack>
      </Box>
    )
  }

  if (isUuid(val) && /(?:^chainId$|ChainId$)/.test(spec.key)) {
    return <ChainLink id={val} />
  }

  return (
    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
      {String(val)}
    </Typography>
  )
}

interface IProps {
  specs: UserbotCatalog.FieldSpec[]
  value: unknown
}

/**
 * Generic FieldSpec-рендерер результата шага (SPEC §4.2).
 * Рендерит структуры без привязки к имени действия.
 */
export const FieldSpecResult: FC<IProps> = ({ specs, value }) => {
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  const obj =
    typeof value === "object" ? (value as Record<string, unknown>) : null

  return (
    <Stack spacing={1.5}>
      {specs.map((spec) => {
        const val = isRootUnion(specs) ? value : obj ? obj[spec.key] : value
        return (
          <Paper key={spec.key} variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" mb={0.5}>
              {spec.label || spec.key}
            </Typography>
            {renderValue(spec, val)}
          </Paper>
        )
      })}
    </Stack>
  )
}
